import { Button } from "@mui/material";
import {
	DataGridPremium,
	GridToolbarContainer,
	useGridApiRef,
	GridActionsCellItem
} from '@mui/x-data-grid-premium';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useRef, useCallback, useMemo } from "react";
import strings from "../../Globalization";
import consts from "../../consts";
import connectionService from '../../services/Connection';
import AsyncAutocomplete from "../../components/common/AsyncAutocomplete";
import { useModelForm } from "../../contexts/ModelForm";

const CustomeToolBar = ({ type, handleAddRow }) => {
	return (
		<GridToolbarContainer  sx={{ justifyContent: 'flex-start' }}>
			<Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => (handleAddRow({ type }))}> {strings.Add}</Button>
		</GridToolbarContainer>
	)

}

const CustomDropDown = ({ id, field, hasFocus, api }) => {
	const currentRowIndex = id - 1;
	const { formik, activeRecord = {}, setActiveRecord } = useModelForm();
	const { combos = {} } = activeRecord;
	const { CommandServices = [] } = combos;
	const Services = useMemo(() => formik.values.Services || [], [formik.values.Services]);
	const options = useMemo(
		() =>
			(CommandServices || []).map((option) => ({
				...option,
				value: option.LookupId,
				label: option.DisplayValue
			})),
		[CommandServices]
	);

	const selectedValue = Services[currentRowIndex];
	const optionIndex = selectedValue ? options.findIndex(({ value }) => value === selectedValue.Service) : -1;
	const value = options[optionIndex];
	const DisabledIds = Services.map(({ Service }) => Service).filter((Service) => !!Service);

	const getCommandOptions = async () => {
		let action = 'list';
		let page = 1;
		const formData = new FormData();
		formData.append('action', action);
		formData.append('LookupTypeId', consts.LookupType.CommandServices);
		formData.append('page', page);
		formData.append('limit', 100);
		const res = await fetch(connectionService.Lookup, { method: 'POST', body: formData, credentials: 'include' });
		const { success, records } = await res.json();
		if (success && typeof setActiveRecord === 'function') {
			setActiveRecord((prev) => {
				const prevCombos = prev.combos || {};
				return {
					...prev,
					combos: {
						...prevCombos,
						CommandServices: records
					}
				};
			});
		}
	};
	const onCreateOption = (LookupTypeId) => async (value) => {
		let action = 'save';
		let id = 0;
		const formData = new FormData();
		formData.append('action', action);
		formData.append('id', id);
		formData.append('LookupTypeId', LookupTypeId);
		formData.append('CustomStringValue', value.option.id);
		formData.append('DisplayValue', value.option.id);
		try {
			const response = await fetch(connectionService.Lookup, {
				method: 'POST',
				body: formData,
				credentials: 'include'
			});
			if (response.ok) {
				const result = await response.json();
				const newOption = result.data; // Assuming the new option is returned in the records array
				// Update the options and set the newly created option as the selected value
				setActiveRecord((prev) => {
					const prevCombos = prev.combos || {};
					return {
						...prev,
						combos: {
							...prevCombos,
							CommandServices: [...(prevCombos.CommandServices || []), newOption]
						}
					}
				});
				// Update the Services array with the new selected value
				formik.setFieldValue(`Services[${currentRowIndex}].Service`, newOption.LookupId);

				getCommandOptions();
			} else {
				console.error('Failed to create record:', response.statusText);
			}
		} catch (error) {
			console.error('Error creating record:', error);
		}
	};

	const handleChange = useCallback(
		async (event, newValue = {}) => {
			await api.setEditCellValue({ id, field, value: newValue.value });
			api.stopCellEditMode({ id, field });
		},
		[id, field, api]
	);

	const getOptionDisabled = useCallback(
		(option) => {
			const id = option.value;
			const optionDisabled = DisabledIds.includes(id);
			return optionDisabled;
		},
		[DisabledIds]
	);
	return (
		<AsyncAutocomplete
			value={value}
			onChange={handleChange}
			options={options}
			open={hasFocus}
			sx={{
				width: 300,
				'& .MuiOutlinedInput-root': {
					'& fieldset': {
						border: 'none'
					}
				}
			}}
			getOptionDisabled={getOptionDisabled}
			freeSolo
			onCreateOption={onCreateOption(consts.LookupType.CommandServices)}
		/>
	);
};

const CommandServicesDataGrid = () => {
	
	const { activeRecord = {}, formik } = useModelForm();
	const { combos = {} } = activeRecord;
	const { CommandServices = [] } = combos;
	const Services = useMemo(() => (Array.isArray(formik.values.Services) ? formik.values.Services : []), [formik.values.Services]);
	const apiRef = useGridApiRef();
	const cellInEditRef = useRef(null);
	
	const {errors = {}, touched = {}} = formik.errors;
	const fieldTouched = touched.Services;
	const error = errors.Services;
	
	const sx = useMemo(
		() => ({
			minHeight: 200,
			borderColor: error ? 'error.main' : 'divider'
		}),
		[error]
	);

	const CommandServicesLabels =  useMemo(() => {
		const CommandServicesList = Array.isArray(CommandServices)? CommandServices : [];
		return CommandServicesList.reduce((acc, {LookupId, DisplayValue}) => {
			acc[LookupId] = DisplayValue;
			return acc;
		}, {});
	}, [CommandServices]);

	

	const options = activeRecord?.combos?.CommandLogLevel.map(level => ({
		value: level.LookupId,
		label: level.DisplayValue
	})) || [];


	const handleRemove = useCallback(
		(id) => {
			const updatedRows = Services.filter((row) => row.id !== id);
			updatedRows.forEach((row) => {
				if (row.id > id) {
					row.id--;
				}
			});
			formik.setFieldValue('Services', updatedRows);
			if (!fieldTouched) {
				formik.setFieldTouched('Services', true);
			}
		},
		[Services, fieldTouched, formik]
	);

	
	
	const columns = useMemo(
		() => [
			{
				field: 'LogLevel',
				headerName: strings.LogLevel,
				width: 160,
				editable: true,
				type: 'singleSelect',
				valueOptions: options
			},
			{
				field: 'Service',
				headerName: strings.Services,
				width: 300,
				editable: true,
				valueFormatter: (value) => CommandServicesLabels[value],
				renderEditCell: (params) => <CustomDropDown {...params} />
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				width: 100,
				getActions: ({ id }) => [
					<GridActionsCellItem
						key={id}
						icon={<DeleteIcon />}
						color="error"
						onClick={() => handleRemove(id)}
						disabled={Services.length === 1}
						label="Delete"
					/>
				]
			}
		],
		[options, CommandServicesLabels, Services.length, handleRemove]
	);

	
	const handleAddRow = useCallback(async () => {
		const hasDefaultLogLevelService = Services.some(({ Service }) => Service === consts.DefaultLogLevelService);
		const value = [
			...Services,
			{
				id: Services.length + 1,
				LogLevel: consts.infoLevel,
				Service: !hasDefaultLogLevelService ? consts.DefaultLogLevelService : ''
			}
		];
		formik.setFieldValue('Services', value);
		if (!fieldTouched) {
			formik.setFieldTouched('Services', true);
		}
		
	}, [Services, formik, fieldTouched]);


	const processRowUpdate = useCallback(
		(updatedRow, originalRow) => {			
		const updateIndex = updatedRow.id - 1;
		const updatedServices = [...Services];
		updatedServices[updateIndex] = updatedRow;
		formik.setFieldValue('Services', updatedServices);
			cellInEditRef.current = null;
			if (!fieldTouched) {
				formik.setFieldTouched('Services', true);
			}
			return updatedRow;
		},
		[Services, fieldTouched, formik]
	);

	const onCellEditStart = useCallback(
		({ colDef, id, row }) => {			
			let index = id - 1; // getting index of record
			index = index === -1 ? 0 : index;
			cellInEditRef.current = { field: colDef.field, index, row };
		},
		[]
	);

	

	return (
		<DataGridPremium
			rows={Services}
			columns={columns}
			sx={sx}
			slots={{
				toolbar: CustomeToolBar
			}}
			slotProps={{
				toolbar: {
					type: 'Services',
					handleAddRow
				}
			}}
			apiRef={apiRef}
			processRowUpdate={processRowUpdate}
			onCellEditStart={onCellEditStart}
			showToolbar
			disablePivoting
			disableAggregation
		/>
	);
}

export default CommandServicesDataGrid